import React from "react";
import { Shield, Sparkles, SprayCan, Droplet, Car, Star } from "lucide-react";

const services = [
  {
    id: "exterior",
    title: "Exterior Detail",
    short: "Wash, decontaminate, polish.",
    description:
      "Hand wash, iron fallout removal, clay bar, sealant or wax finish. Brings back the deep gloss.",
    icon: Car,
    image:
      "https://images.unsplash.com/photo-1754973277204-9b530bb01ef0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBjYXIlMjBkZXRhaWxpbmclMjBkYXJrfGVufDB8fHx8MTc3NzY1OTAyNHww&ixlib=rb-4.1.0&q=85",
    span: "lg:col-span-4 lg:row-span-2",
    feature: true,
  },
  {
    id: "interior",
    title: "Interior Deep Clean",
    short: "Steam, extract, protect.",
    description:
      "Shampoo carpets, steam upholstery, condition leather, sanitize every touch-point.",
    icon: Sparkles,
    image:
      "https://images.unsplash.com/photo-1771491237209-b89bc7290588?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwyfHxjYXIlMjBpbnRlcmlvciUyMGNsZWFuaW5nJTIwZGV0YWlsaW5nfGVufDB8fHx8MTc3NzY1OTAyNHww&ixlib=rb-4.1.0&q=85",
    span: "lg:col-span-2 lg:row-span-2",
  },
  {
    id: "full",
    title: "Full Detail",
    short: "Inside and out.",
    description:
      "The complete treatment — exterior polish plus interior deep clean in a single appointment.",
    icon: Star,
    span: "lg:col-span-2",
  },
  {
    id: "ceramic",
    title: "Ceramic Coating",
    short: "Up to 7 years of gloss.",
    description:
      "Professional-grade ceramic with deep hydrophobic properties and UV protection.",
    icon: Shield,
    image:
      "https://images.unsplash.com/photo-1654522660328-eede2194e699?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwyfHxjYXIlMjBjZXJhbWljJTIwY29hdGluZyUyMHNoaW5lfGVufDB8fHx8MTc3NzY1OTAyNHww&ixlib=rb-4.1.0&q=85",
    span: "lg:col-span-4 lg:row-span-2",
    feature: true,
  },
  {
    id: "paint",
    title: "Paint Correction",
    short: "Remove swirls, scratches.",
    description:
      "Multi-stage machine polishing to restore factory-fresh clarity and depth.",
    icon: SprayCan,
    span: "lg:col-span-3",
  },
  {
    id: "addon",
    title: "Add-Ons",
    short: "Engine · Headlights · Pet Hair",
    description:
      "Custom single-service add-ons for targeted problem areas.",
    icon: Droplet,
    span: "lg:col-span-3",
  },
];

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 md:py-32 bg-[#050505]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="w-12 h-px bg-red-500" />
              <span className="text-xs tracking-[0.3em] uppercase text-red-500 font-bold">
                Our Craft
              </span>
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter max-w-3xl">
              Services built around <br />
              <span className="text-zinc-600">your vehicle.</span>
            </h2>
          </div>
          <p className="text-zinc-400 max-w-md text-base md:text-lg leading-relaxed">
            À la carte options for when you need something specific, or bundle
            them into one of our signature packages below.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 md:gap-5 auto-rows-[220px]">
          {services.map((s) => {
            const Icon = s.icon;
            return (
              <article
                key={s.id}
                data-testid={`service-card-${s.id}`}
                className={`relative group overflow-hidden border border-zinc-900 hover:border-red-500/60 bg-zinc-950 transition-all duration-500 ${s.span || ""}`}
              >
                {s.image && (
                  <>
                    <img
                      src={s.image}
                      alt={s.title}
                      className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-60 group-hover:scale-105 transition-all duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/20" />
                  </>
                )}
                <div className="relative h-full p-8 md:p-10 flex flex-col justify-between">
                  <div className="flex items-start justify-between">
                    <div className="w-12 h-12 border border-zinc-800 group-hover:border-red-500 flex items-center justify-center transition-colors">
                      <Icon
                        size={20}
                        className="text-zinc-400 group-hover:text-red-500 transition-colors"
                      />
                    </div>
                    <span className="text-xs tracking-[0.25em] uppercase text-zinc-600">
                      {String(services.indexOf(s) + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-display text-2xl md:text-3xl uppercase tracking-tight mb-2">
                      {s.title}
                    </h3>
                    <p className="text-zinc-400 text-sm md:text-base leading-relaxed max-w-md">
                      {s.feature ? s.description : s.short}
                    </p>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
